import { useSearch } from "@tanstack/react-router";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Spinner, Text } from "@/shared/ui";
import { useChatRun } from "../hooks/ChatRunProvider";
import { useChatAssistant } from "../hooks/useChatAssistant";
import { useComposerAttachments } from "../hooks/useComposerAttachments";
import { useConversationDetail } from "../hooks/useConversationDetail";
import { useMe } from "../hooks/useMe";
import { useModels, useModelUsage } from "../hooks/useModels";
import { useMyRunSkills } from "../hooks/useRunSkills";
import { composerAttachmentViews } from "../lib/attachments";
import {
  modelAllowedForPlan,
  modelLabel,
  pickDefaultModelId,
  type PlanTier,
  requiredPlanFor,
} from "../lib/models";
import { openPaywall } from "@/shared/lib/paywall";
import { resolveSlashSkill, stripLeadingSlashToken } from "../lib/slashSkills";
import { buildThread, runOwnsView } from "../lib/thread";
import { AssistantWelcome } from "./AssistantWelcome";
import { Composer } from "./Composer";
import { ModelPicker } from "./ModelPicker";
import { PageFileDrop } from "./PageFileDrop";
import { Thread } from "./Thread";
import { WelcomeState } from "./WelcomeState";
import { TopbarLeading } from "@/shared/ui";
import { useFilePaste } from "@/shared/lib/hooks/useFilePaste";

/**
 * The chat surface for one conversation (or the welcome route when `null`).
 * Merges the server thread with the in-flight run, picks the welcome vs thread
 * layout, defaults the model, and owns the composer draft.
 */
export function ChatView({ conversationId }: { conversationId: string | null }) {
  const { t } = useTranslation();
  const {
    run,
    send,
    stop,
    isStreaming,
    queuedSends,
    removeQueuedSend,
    selectedModelId,
    setSelectedModelId,
    setActiveSkillIds,
  } = useChatRun();
  const detail = useConversationDetail(conversationId);
  const { data: me } = useMe();
  const { data: modelsData } = useModels();
  const { data: usageData } = useModelUsage();
  const { data: mySkillsData } = useMyRunSkills();
  const attachmentsApi = useComposerAttachments();
  useFilePaste(attachmentsApi.addFiles);
  const [draft, setDraft] = useState("");
  const [webSearch, setWebSearch] = useState(false);
  const [sendNonce, setSendNonce] = useState(0);

  // Object URLs of the just-sent attachments, owned here so the optimistic user
  // bubble keeps its preview. Revoked when the next send starts (server data has
  // loaded by then) and on unmount.
  const inflightUrlsRef = useRef<string[]>([]);
  useEffect(() => {
    const ref = inflightUrlsRef;
    return () => {
      for (const url of ref.current) URL.revokeObjectURL(url);
    };
  }, []);

  // A new chat can be scoped to an assistant via `?assistant=<id>` (set from its
  // detail page). Only meaningful on the welcome route — once a conversation
  // exists it carries the assistant server-side.
  const search = useSearch({ strict: false }) as { assistant?: string; skill?: string };
  const assistantId = conversationId === null ? (search.assistant ?? null) : null;
  const assistantQuery = useChatAssistant(assistantId);
  const assistant = assistantQuery.data;

  // `?skill=<id>` (a skill detail's "Use in chat") pre-selects that skill in
  // the composer picker. Seeded once per id — the user stays free to unpick.
  const seedSkillId = conversationId === null ? (search.skill ?? null) : null;
  useEffect(() => {
    if (!seedSkillId) return;
    setActiveSkillIds((prev) =>
      prev.includes(seedSkillId) ? prev : [...prev, seedSkillId].slice(-3),
    );
  }, [seedSkillId, setActiveSkillIds]);

  // A new assistant chat seeds the web-search toggle from the assistant's
  // default (once, when it loads). The user stays free to toggle it back off;
  // plan gating still applies via `effectiveWebSearch` below.
  const assistantSeededRef = useRef<string | null>(null);
  useEffect(() => {
    if (!assistant || assistantSeededRef.current === assistant.id) return;
    assistantSeededRef.current = assistant.id;
    if (assistant.default_web_search) setWebSearch(true);
  }, [assistant]);

  const models = useMemo(() => modelsData?.items ?? [], [modelsData]);
  const usage = useMemo(() => usageData?.items ?? [], [usageData]);
  const mySkills = useMemo(() => mySkillsData ?? [], [mySkillsData]);
  const conversationModelId = detail.data?.conversation.model_id ?? null;

  // Web search is a paid-plan feature; free users can't toggle it on. Gate both
  // the composer control and the value we send so the backend never sees a
  // free-tier web_search request.
  const canWebSearch = me?.plan_id != null && me.plan_id !== "free";
  const effectiveWebSearch = webSearch && canWebSearch;

  // Default-select a model when none is chosen, biased to the assistant's
  // default model (new assistant chat), else the open conversation's. Wait for
  // the assistant to load before picking so its preference isn't missed.
  useEffect(() => {
    if (models.length === 0) return;
    // A model may already be chosen from a prior session (persisted) — keep it,
    // but only if it's still in the catalog; otherwise re-pick.
    if (selectedModelId && models.some((m) => m.id === selectedModelId)) return;
    if (assistantId && assistantQuery.isLoading) return;
    const preferred = assistant?.default_model ?? conversationModelId;
    const next = pickDefaultModelId(models, usage, preferred);
    if (next) setSelectedModelId(next);
  }, [
    selectedModelId,
    models,
    usage,
    conversationModelId,
    setSelectedModelId,
    assistantId,
    assistant,
    assistantQuery.isLoading,
  ]);

  const showRun = runOwnsView(run, conversationId);
  const items = buildThread(detail.data?.messages ?? [], run, conversationId);

  function handleSend() {
    // Gate before sending: if the chosen model isn't on the user's plan, open
    // the paywall (not an inline error after a wasted round-trip). This catches
    // models auto-selected from an old conversation or a since-downgraded plan.
    const activeModel = models.find((m) => m.id === selectedModelId);
    if (activeModel && me?.plan_id && !modelAllowedForPlan(activeModel, me.plan_id as PlanTier)) {
      const plan = requiredPlanFor(activeModel);
      if (plan) openPaywall({ kind: "model", modelName: modelLabel(activeModel), plan });
      return;
    }

    // A leading `/<slug-or-name>` scopes this turn to one of the user's skills:
    // resolve it, strip the token from the outgoing text, and ride it as a
    // per-turn `skill_ids` override. No match → send the draft verbatim.
    const matched = resolveSlashSkill(draft, mySkills);
    const text = matched ? stripLeadingSlashToken(draft) : draft;
    const skillIds = matched ? [matched.id] : undefined;

    const hasText = text.trim().length > 0;
    const hasReady = attachmentsApi.attachments.some((a) => a.status === "ready");
    if (attachmentsApi.isUploading || (!hasText && !hasReady)) return;

    const ready = attachmentsApi.take();
    // Hand off the new previews; the previous batch's server data has loaded.
    for (const url of inflightUrlsRef.current) URL.revokeObjectURL(url);
    inflightUrlsRef.current = ready
      .map((a) => a.previewUrl)
      .filter((url): url is string => Boolean(url));

    // Mirror the resolved skill into the shared selection so the UI/state stays
    // consistent; the run itself uses the explicit override (set this tick).
    if (matched) setActiveSkillIds([matched.id]);

    setDraft("");
    send({
      conversationId,
      text,
      webSearch: effectiveWebSearch,
      // New assistant chats inherit the assistant's code-execution default; once
      // the conversation exists the backend carries the assistant itself.
      codeExecution: Boolean(assistantId && assistant?.default_code_execution),
      fileIds: ready.map((a) => a.fileId as string),
      attachments: composerAttachmentViews(ready),
      assistantId,
      ...(skillIds ? { skillIds } : {}),
    });
    setSendNonce((n) => n + 1);
  }

  const composer = (variant: "welcome" | "dock") => (
    <div>
      {queuedSends.length > 0 ? (
        <div className="mx-auto mb-2 flex max-w-full flex-wrap items-center gap-2 px-1">
          {queuedSends.map((queued) => (
            <span
              key={queued.queueId}
              className="inline-flex max-w-[260px] items-center gap-1.5 rounded-full bg-bubble px-3 py-1 text-[12px] text-ink-soft"
            >
              <span className="text-ink-faint">{t("chat.queuedLabel")}</span>
              <span className="truncate">{queued.text || "…"}</span>
              <button
                type="button"
                aria-label={t("common.remove")}
                onClick={() => removeQueuedSend(queued.queueId)}
                className="ml-0.5 text-ink-faint transition-colors hover:text-ink"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <Composer
      value={draft}
      onChange={setDraft}
      onSubmit={handleSend}
      onStop={stop}
      isStreaming={isStreaming}
      modelId={selectedModelId}
      webSearch={webSearch}
      onToggleWebSearch={() => setWebSearch((on) => !on)}
      canWebSearch={canWebSearch}
      attachments={attachmentsApi.attachments}
      onAddFiles={attachmentsApi.addFiles}
      onRemoveAttachment={attachmentsApi.remove}
      attachmentsUploading={attachmentsApi.isUploading}
      variant={variant}
    />
    </div>
  );

  let body: ReactNode;
  if (conversationId === null && !showRun) {
    if (assistantId && assistantQuery.isLoading) {
      body = (
        <div className="flex min-h-full items-center justify-center">
          <Spinner label={t("chat.loading")} />
        </div>
      );
    } else if (assistant) {
      body = (
        <AssistantWelcome
          assistant={assistant}
          composer={composer("welcome")}
          onChipSelect={setDraft}
        />
      );
    } else {
      body = <WelcomeState composer={composer("welcome")} onChipSelect={setDraft} />;
    }
  } else if (conversationId !== null && !showRun && detail.isPending) {
    body = (
      <div className="flex min-h-full items-center justify-center">
        <Spinner label={t("chat.loading")} />
      </div>
    );
  } else if (conversationId !== null && !showRun && detail.isError) {
    body = (
      <div className="flex min-h-full flex-col items-center justify-center gap-3 px-6 text-center">
        <Text variant="body" className="text-ink-soft">
          {t("chat.thread.loadError")}
        </Text>
        <button
          type="button"
          onClick={() => void detail.refetch()}
          className="text-[13px] font-medium text-ink underline-offset-2 hover:underline"
        >
          {t("errors.tryAgain")}
        </button>
      </div>
    );
  } else {
    body = (
      <div className="flex min-h-full flex-col">
        <Thread
          items={items}
          conversationId={conversationId}
          webSearch={effectiveWebSearch}
          sendNonce={sendNonce}
        />
        <div className="sticky bottom-0 z-[5] bg-gradient-to-t from-canvas from-[62%] to-transparent px-6 pb-5 pt-2 max-[880px]:px-4">
          <div className="mx-auto w-full max-w-[760px]">{composer("dock")}</div>
          <p className="mt-3 text-center font-sans text-[11px] tracking-[0.04em] text-ink-faint">
            {t("chat.composer.disclaimer")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <TopbarLeading>
        <ModelPicker value={selectedModelId} onChange={setSelectedModelId} side="bottom" />
      </TopbarLeading>
      {body}
      <PageFileDrop onFiles={attachmentsApi.addFiles} />
    </>
  );
}
